<template>
  <div class="controls">
    <select v-model="level" aria-label="Niveau minimal" @change="load">
      <option value="">Tous les niveaux</option>
      <option value="warn">Avertissements et erreurs</option>
      <option value="error">Erreurs uniquement</option>
    </select>
    <input v-model="search" type="search" placeholder="Rechercher dans les logs" aria-label="Rechercher dans les logs" @input="searchLater" @keyup.enter="load" />
    <select v-model="limit" aria-label="Nombre de lignes" @change="load">
      <option v-for="option in LIMITS" :key="option" :value="option">{{ option }} lignes</option>
    </select>
    <label class="auto"><input type="checkbox" v-model="auto" /> Actualiser toutes les 10 s</label>
    <div class="reload">
      <CustomButton :params="{text: 'Actualiser', loading: isLoading}" :isSecondary="true" :isCompact="true" @click="load" />
    </div>
  </div>
  <div v-if="hasFailed" class="notice">Les logs n'ont pas pu être chargés.</div>
  <div v-else-if="logs && !logs.entries.length" class="notice">Aucune ligne de log ne correspond.</div>
  <ol v-else-if="logs" class="logs" aria-label="Logs du container">
    <li v-for="(entry, index) in lines" :key="index" class="log" :class="entry.level">
      <span class="log-time">{{ entry.timestamp.slice(0, 19) }}</span>
      <span class="log-level">{{ entry.level }}</span>
      <pre class="log-message">{{ entry.message }}</pre>
    </li>
  </ol>
  <p v-if="logs" class="footer">
    {{ logs.entries.length }} ligne(s), les plus récentes en premier, heure du container.
    <template v-if="logs.truncated">Seules les dernières lignes sont affichées : affinez la recherche ou augmentez le nombre de lignes.</template>
    <template v-if="logs.files.length"> Fichiers : {{ logs.files.map(file => `${file.name} (${bytes(file.sizeBytes)})`).join(', ') }}.</template>
    <template v-else> Aucun fichier de log n'a été trouvé sur le container.</template>
  </p>
</template>

<script>
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import {AdministrationController} from "@/administration/controllers/AdministrationController";
import {bytes} from "@/administration/utils";

const LIMITS = [100, 200, 500, 1000];

/**
 * Les dernières lignes des logs d'un container, filtrables par niveau et par texte.
 * containerId : container dont on lit les logs (ms-business par exemple).
 */
export default {
  name: 'ContainerLogs',
  components: {CustomButton},
  props: {
    containerId: {type: String, required: true},
  },
  data() {
    return {
      LIMITS,
      level: '',
      search: '',
      limit: 200,
      auto: false,
      logs: undefined,
      isLoading: false,
      hasFailed: false,
      requestId: 0,
      searchTimer: undefined,
      autoTimer: undefined,
    };
  },
  computed: {
    // The most recent first
    lines() {
      return [...this.logs.entries].reverse();
    },
  },
  mounted() {
    this.load();
    this.autoTimer = setInterval(() => {
      if (this.auto && !document.hidden) this.load();
    }, 10000);
  },
  beforeUnmount() {
    clearInterval(this.autoTimer);
    clearTimeout(this.searchTimer);
  },
  methods: {
    bytes,
    // The text is searched once the user stops typing
    searchLater() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.load, 400);
    },
    async load() {
      clearTimeout(this.searchTimer);
      // A late answer of an old request must not replace the last one
      const requestId = ++this.requestId;
      this.isLoading = true;
      const resp = await AdministrationController.getContainerLogs(this.containerId, {level: this.level, search: this.search.trim(), limit: this.limit});
      if (requestId !== this.requestId) return;
      this.hasFailed = !resp || !!resp.errors;
      if (!this.hasFailed) this.logs = resp;
      this.isLoading = false;
    },
  },
};
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.controls
  display flex
  flex-wrap wrap
  align-items center
  gap 10px 12px
  margin-bottom 12px

  select, input[type=search]
    height 38px
    padding 0 10px
    font inherit
    font-size 13.5px
    color #3f3931
    background white
    border 1px solid CTRL_BORDER
    border-radius 8px

  input[type=search]
    flex 1
    min-width 180px

.auto
  display flex
  align-items center
  gap 6px
  font-size 13px
  color CTRL_TEXT_MUTED
  cursor pointer

  input
    accent-color THEME_COLOR_PRIMARY

.reload
  width 120px

.notice
  padding 28px 16px
  text-align center
  color CTRL_TEXT_MUTED
  background CTRL_BG
  border 1px dashed CTRL_BORDER
  border-radius 12px

.logs
  max-height 520px
  margin 0
  padding 0
  overflow auto
  list-style none
  font-family ui-monospace, SFMono-Regular, Menlo, Consolas, monospace
  font-size 12.5px
  background #fbfcfe
  border 1px solid #e5e5e5
  border-radius 10px

.log
  display grid
  grid-template-columns 150px 54px minmax(0, 1fr)
  gap 10px
  padding 4px 10px
  border-bottom 1px solid #f1f3f8
  border-left 3px solid transparent

  &.error
    background CTRL_ERROR_BG
    border-left-color #ea4e2c

  &.warn
    background #fff9ea
    border-left-color #ef9400

  @media only screen and (max-width: TABLET_SIZE)
    grid-template-columns 54px minmax(0, 1fr)

    .log-time
      grid-column 1 / -1

.log-time
  color CTRL_TEXT_MUTED
  white-space nowrap

.log-level
  font-weight 700
  text-transform uppercase
  font-size 11.5px
  color CTRL_TEXT_MUTED

.error .log-level
  color CTRL_ERROR

.warn .log-level
  color #8a5a00

.log-message
  margin 0
  font inherit
  color #3f3931
  white-space pre-wrap
  overflow-wrap anywhere

.footer
  margin 10px 0 0
  font-size 12.5px
  color CTRL_TEXT_MUTED
</style>
